/**
 * Console root. Mounts React, applies the Oxygen theme (light/dark) and the toast
 * layer, bootstraps the operator session once, and routes between the screens. A
 * central 401 handler bounces any expired/gated call back to /login (preserving
 * the intended destination). The browser holds only the HttpOnly session cookie.
 */

import './styles.css';
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import styled from 'styled-components';
import { OxygenProvider } from '@8x8/oxygen-config';
import { Spinner } from '@8x8/oxygen-loaders';
import type { Environment } from '@spaces/contracts';
import { getSession, logout, setUnauthorizedHandler } from './api';
import type { SessionState } from './api';
import { currentHref, navigate, useLocation } from './router';
import { useThemeMode } from './theme';
import { ToastProvider } from './toast';
import { Shell } from './ui/Shell';
import { Login } from './screens/Login';
import { RoomsList } from './screens/RoomsList';
import { RoomCreate } from './screens/RoomCreate';
import { RoomDetail } from './screens/RoomDetail';
import { Alerts } from './screens/Alerts';
import { Calendars } from './screens/Calendars';

const DEFAULT_ENV: Environment = 'dev';

const Booting = styled.div`
    height: 100%;
    background: ${p => p.theme.ui31};
    display: flex;
    align-items: center;
    justify-content: center;
`;

function App(): JSX.Element {
    const { mode, toggle } = useThemeMode();
    const [session, setSession] = useState<SessionState | null>(null);
    const route = useLocation();

    useEffect(() => {
        setUnauthorizedHandler(() => {
            setSession(prev => (prev ? { ...prev, authenticated: false } : { authenticated: false, env: DEFAULT_ENV }));
            if (location.pathname !== '/login') {
                navigate(`/login?expired=1&next=${encodeURIComponent(currentHref())}`, true);
            }
        });

        void getSession().then(result => {
            setSession(result.ok ? result.data : { authenticated: false, env: DEFAULT_ENV });
        });
    }, []);

    // Redirect away from protected routes when there's no session.
    useEffect(() => {
        if (session && !session.authenticated && route.name !== 'login') {
            navigate(`/login?next=${encodeURIComponent(currentHref())}`, true);
        }
    }, [session, route.name, route.path]);

    const env = session?.env ?? DEFAULT_ENV;

    return (
        <OxygenProvider themeName={mode}>
            <ToastProvider>
                {session === null ? (
                    <Booting><Spinner size="large2x" /></Booting>
                ) : route.name === 'login' ? (
                    <Login env={env} onSession={setSession} />
                ) : !session.authenticated ? (
                    <Booting><Spinner size="large2x" /></Booting>
                ) : (
                    <Shell
                        env={env}
                        tenant={session.tenant ?? null}
                        identity={session.identity?.loginId ?? null}
                        themeMode={mode}
                        onToggleTheme={toggle}
                        onSignOut={() => {
                            void logout().then(() => {
                                setSession({ authenticated: false, env });
                                navigate('/login', true);
                            });
                        }}
                    >
                        <Screen session={session} />
                    </Shell>
                )}
            </ToastProvider>
        </OxygenProvider>
    );
}

/** Pick the content screen for the current authenticated route. */
function Screen({ session }: { session: SessionState }): JSX.Element {
    const route = useLocation();

    switch (route.name) {
        case 'create':
            return <RoomCreate session={session} />;
        case 'detail':
            return <RoomDetail roomId={route.roomId ?? ''} session={session} />;
        case 'alerts':
            return <Alerts session={session} />;
        case 'calendars':
            return <Calendars />;
        case 'rooms':
            return <RoomsList session={session} />;
        default:
            return <RoomsList session={session} />;
    }
}

const container = document.getElementById('app');

if (container) {
    container.textContent = '';
    createRoot(container).render(
        <StrictMode>
            <App />
        </StrictMode>
    );
}
